import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

const EmpresaContext = createContext(null)

export function EmpresaProvider({ children }) {
  const { user } = useAuth()

  const [empresas, setEmpresas] = useState([])
  const [empresaActual, setEmpresaActual] = useState(null)
  const [loadingEmpresas, setLoadingEmpresas] = useState(true)
  const [errorEmpresas, setErrorEmpresas] = useState('')

  useEffect(() => {
    if (!user) {
      setEmpresas([])
      setEmpresaActual(null)
      setLoadingEmpresas(false)
      return
    }

    cargarEmpresas()
    // Se depende del id (no del objeto user completo) para no volver a
    // cargar las empresas cuando Supabase emite un nuevo objeto de sesión
    // (por ejemplo TOKEN_REFRESHED) sin que el usuario haya cambiado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const cargarEmpresas = async () => {
    try {
      setLoadingEmpresas(true)
      setErrorEmpresas('')

      const { data, error } = await supabase
        .from('usuarios_empresas')
        .select(`
          id,
          rol,
          es_predeterminada,
          activo,
          empresa:empresas (
            id,
            razon_social,
            nombre_fantasia,
            rut,
            activo
          )
        `)
        .eq('usuario_id', user.id)
        .eq('activo', true)

      if (error) {
        throw error
      }

      const relacionesValidas = (data || []).filter(
        (registro) => registro.empresa && registro.empresa.activo
      )

      const empresasUsuario = relacionesValidas.map((registro) => ({
        relacionId: registro.id,
        rol: registro.rol,
        esPredeterminada: registro.es_predeterminada,
        ...registro.empresa,
      }))

      setEmpresas(empresasUsuario)

      if (empresasUsuario.length === 0) {
        setEmpresaActual(null)
        return
      }

      const empresaGuardadaId = localStorage.getItem(
        'francofrutt_empresa_id'
      )

      const empresaGuardada = empresasUsuario.find(
        (empresa) => empresa.id === empresaGuardadaId
      )

      const empresaPredeterminada = empresasUsuario.find(
        (empresa) => empresa.esPredeterminada
      )

      const empresaInicial =
        empresaGuardada ||
        empresaPredeterminada ||
        empresasUsuario[0]

      localStorage.setItem('francofrutt_empresa_id', empresaInicial.id)
      setEmpresaActual(empresaInicial)
    } catch (error) {
      console.error('Error al cargar empresas:', error)

      setErrorEmpresas(
        error.message ||
          'No fue posible cargar las empresas del usuario.'
      )

      setEmpresas([])
      setEmpresaActual(null)
    } finally {
      setLoadingEmpresas(false)
    }
  }

  const seleccionarEmpresa = (empresaId) => {
    const empresaSeleccionada = empresas.find(
      (empresa) => empresa.id === empresaId
    )

    if (!empresaSeleccionada) {
      return
    }

    localStorage.setItem(
      'francofrutt_empresa_id',
      empresaSeleccionada.id
    )

    setEmpresaActual(empresaSeleccionada)
  }

  const value = useMemo(
    () => ({
      empresas,
      empresaActual,
      loadingEmpresas,
      errorEmpresas,
      seleccionarEmpresa,
      recargarEmpresas: cargarEmpresas,
    }),
    [
      empresas,
      empresaActual,
      loadingEmpresas,
      errorEmpresas,
    ]
  )

  return (
    <EmpresaContext.Provider value={value}>
      {children}
    </EmpresaContext.Provider>
  )
}

export function useEmpresa() {
  const context = useContext(EmpresaContext)

  if (!context) {
    throw new Error(
      'useEmpresa debe utilizarse dentro de EmpresaProvider'
    )
  }

  return context
}